import { initConsoleLogger } from '@salsita-ai/platform-common/utils';
import { CollisionProjections, ComponentReader, DimensionSystem, ResourceBuilder, SketchingProjections } from '@salsita-ai/platform-project-sdk/domain';
import { adminDomainBuilder, adminInvariantBuilder } from './__generated/admin-implementation.js';
import { generatedComponents } from './__generated/components/index.js';
import { generatedConfiguratorBuilder } from './__generated/configurator-builder.js';
import { domainBuilder, invariantBuilder } from './__generated/implementation.js';
import { countersinkComponents as adminCountersinkComponents } from './admin/implementation/countersink.js';
import { customMaterialExpressions } from './admin/implementation/custom-material.js';
import { featureSettingExpressions, featureSettingInvariants } from './admin/implementation/feature-settings.js';
import { migrateAdmin } from './admin/migrations/index.js';
import { anchorPointExpressions } from './implementation/anchor-point.js';
import { anyCountersinkHoleExpressions } from './implementation/any-countersink-hole.js';
import { customBendExpressions } from './implementation/bends.js';
import { camlockHoleExpressions } from './implementation/camlock-hole.js';
import { circularHoleExpressions } from './implementation/circular-hole.js';
import { euroCylinderHoleExpressions } from './implementation/euro-cylinder.js';
import { holeExpressions, holeInvariants } from './implementation/hole.js';
import { kickPlateBulkOrderItemExpressions, kickPlateBulkOrderItemInvariants } from './implementation/kick-plate-bulk-order-item.js';
import { kickPlateFixingHoleExpressions } from './implementation/kick-plate-fixing-holes.js';
import { ovalCylinderHoleExpressions } from './implementation/oval-cylinder.js';
import { plateExpressions, plateInvariants } from './implementation/plate.js';
import { migrateState } from './migrations/index.js';
export * from './__generated/enhanced-model.js';
export * from './__generated/model/index.js';
export { MetalworksTokenMissingError, createMetalworksApiClient } from './services/metalworks-api.js';
export * from './services/metalworks-api.types.js';
export { attachComponent, createRecipe } from './__generated/implementation.js';
export { migrateState } from './migrations/index.js';
export { CustomResource, unfoldedResourceEnabled } from './resources.js';
export { holeLabels as featureLabels } from './implementation/hole.js';
export { defaultCountersinks, defaultCustomMaterials } from './data/plate-data.js';
export { finishLabels, maxMachinePlateSize, minDistanceToEdge, rawMaterialLabels } from './implementation/plate.js';
export { radiusToSegmentCount } from './polygons/shapes.js';
export { findCircleIntersections } from './polygons/utils.js';
export { getHolePositions } from './util/hole-positions.js';
export { rotateVectorOntoBentArm } from './util/bend-vector.js';
export { round } from './util/round.js';
export { getHorizontalHoleCountLabel, getVerticalHoleCountLabel } from './data/plate-data.js';
export { bendData, calculateCustomBendLine, calculateBendLengths } from './data/bend-data.js';
const adminViolationExpressions = adminInvariantBuilder.withInvariants(featureSettingInvariants).buildViolationExpressions();
const adminDomainFactory = adminDomainBuilder.withExpressions(adminViolationExpressions).withExpressions(customMaterialExpressions).withExpressions(featureSettingExpressions).withMigrate(migrateAdmin).build();
const violationExpressions = invariantBuilder.withInvariants(holeInvariants).withInvariants(plateInvariants).withInvariants(kickPlateBulkOrderItemInvariants).buildViolationExpressions();
export const domainFactory = domainBuilder.withExpressions(plateExpressions).withExpressions(anyCountersinkHoleExpressions).withExpressions(camlockHoleExpressions).withExpressions(customBendExpressions).withExpressions(euroCylinderHoleExpressions).withExpressions(ovalCylinderHoleExpressions).withExpressions(circularHoleExpressions).withExpressions(violationExpressions).withExpressions(anchorPointExpressions).withExpressions(holeExpressions).withExpressions(kickPlateFixingHoleExpressions).withExpressions(kickPlateBulkOrderItemExpressions).withMigrate(migrateState).build();
const resources = new ResourceBuilder().addLink({
    type: 'ConfigurationLink',
    name: 'ownerLink'
}).addLink({
    type: 'ConfigurationLink',
    name: 'readRevisionLink'
}).addPdf({
    type: 'Pdf',
    name: 'pdf',
    s3Key: `pdf`
}).addClientGeneratedImage({
    type: 'Image',
    clientGenerated: true,
    name: 'image',
    s3Key: `image`
}).addClientGeneratedImage({
    type: 'Image',
    clientGenerated: true,
    name: 'design',
    s3Key: `design`
}).build();
export const configuratorBuilder = generatedConfiguratorBuilder.withDomainFactory(domainFactory).withAdminDomainFactory(adminDomainFactory).withAdminComponentOptics(adminCountersinkComponents).withResources(resources);
export const unitTestDomain = domainFactory.createDomain({
    publishedData: {},
    sessionData: {
        accessToken: 'test'
    },
    mode: 'owner',
    role: 'anonymous',
    componentReader: ()=>componentReader,
    collisionProjections: ()=>{
        return new CollisionProjections({
            componentReader,
            dimensionSystem
        });
    },
    frontendData: {},
    backendData: {},
    sketchingProjections: ()=>sketchingProjections
});
const componentReader = new ComponentReader({
    domain: unitTestDomain,
    componentOptics: generatedComponents
});
const logger = initConsoleLogger();
const sketchingProjections = new SketchingProjections({
    componentReader
});
const dimensionSystem = new DimensionSystem({
    componentReader,
    logger
});
export * as AdminEnhancedDomain from './__generated/admin-enhanced-model.js';
export * as AdminModel from './__generated/admin-model/index.js';
export * as ApplicationTypes from './__generated/application-types.js';
export * as EnhancedDomain from './__generated/enhanced-model.js';
export * as Model from './__generated/model/index.js';
export { ProductPresets, getRectangularShapeAnchorPointRecipes, getRoundShapeAnchorPointRecipes } from './recipes/plate.presets.js';
export { kickPlateFeatureTypeOptions, otherFeatureTypeOptions, plateTypeOptions } from './admin/implementation/feature-settings.js';

//# sourceMappingURL=index.js.map